import React from "react";
import { Linking, Platform, Pressable, StyleSheet, View } from "react-native";
import { DownloadSimpleIcon } from "@phosphor-icons/react";
import * as Sentry from "@sentry/react-native";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import type { PublishManualStep } from "../types/reviewTypes";

interface ManualStepsTabProps {
  steps: PublishManualStep[];
}

async function openDownloadUrl(url: string): Promise<void> {
  try {
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    const canOpenUrl = await Linking.canOpenURL(url);
    if (!canOpenUrl) {
      Sentry.captureException(new Error("Unsupported manual step download URL"));
      return;
    }

    await Linking.openURL(url);
  } catch (error) {
    Sentry.captureException(error);
  }
}

function ManualStepsTab({ steps }: ManualStepsTabProps) {
  return (
    <View style={styles.container}>
      <CustomText
        text={`${steps.length} manual step${steps.length === 1 ? "" : "s"} for this item`}
        useTranslationText={false}
        size="xs"
        style={styles.headerCount}
      />
      <View style={styles.list}>
        {steps.map((step, index) => (
          <View key={step.id} style={[styles.card, index > 0 && styles.cardBorder]}>
            <CustomText text={step.title} useTranslationText={false} size="xs" weight="medium" style={styles.title} />
            <CustomText text={step.category} useTranslationText={false} size="xs" style={styles.category} />
            <CustomText text={step.detail} useTranslationText={false} size="xs" style={styles.detail} />
            {step.downloadUrl ? (
              <View style={styles.actions}>
                <Pressable
                  onPress={() => {
                    void openDownloadUrl(step.downloadUrl!);
                  }}
                  style={styles.downloadButton}
                  accessibilityRole="button"
                  accessibilityLabel={`Download ${step.title}`}>
                  <DownloadSimpleIcon size={10} color={colors.grey[600]} weight="regular" />
                  <CustomText
                    text="Download"
                    useTranslationText={false}
                    size="xs"
                    weight="medium"
                    style={styles.downloadButtonText}
                  />
                </Pressable>
              </View>
            ) : null}
          </View>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  headerCount: {
    color: colors.grey[500],
  },
  list: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: colors.white,
  },
  card: {
    padding: 12,
    gap: 2,
  },
  cardBorder: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  title: {
    color: colors.grey[900],
  },
  category: {
    color: colors.grey[500],
  },
  detail: {
    color: colors.grey[600],
    lineHeight: 18,
    marginTop: 4,
  },
  actions: {
    flexDirection: "row",
    gap: 8,
    marginTop: 6,
  },
  downloadButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
  },
  downloadButtonText: {
    color: colors.grey[600],
    fontSize: 11,
  },
});

export default ManualStepsTab;
